import { flowRight, get, set } from 'lodash/fp';
import { connect } from 'react-redux';
import { formValues } from 'redux-form';
import { branch, renderNothing } from 'recompose';
import { saveDataInvestor } from '../../actions';
import dataForm from '../dataForm';
import scrollToFirstError from '../form/scrollToFirstError';
import withRouter from '../withRouter';
import cleanInvestorProfileData from '../../utils/cleanInvestorProfileData';

const validTargets = ['client', 'partner', 'client&partner'];

const requiredField = (value) => (!value ? 'Vous devez choisir une réponse.' : null);

const validators = {
    investmentTargets: (value) => ((!value || value.length < 3)
        ? 'Vous devez renseigner au moins trois objectifs.'
        : null),
    ...([
        'financialKnowledge',
        'legalKnowledge',
        'investmentKnowledge',
        'investmentPossession',
        'possibilityOfSouscription',
        'yesNoConductFinancialTransactionsWithoutAdvisor',
        'yesNoConductFinancialTransactionsWithAdvisor',
        'yesNoDelegatingPortfolio',
        'doneInvestmentTypes',
        'investmentInFinancialSupport',
        'yesNoTradingLossKnowledge',
        'downwardPrediction',
        'sufferTradingLosses',
        'doneReactionOnLoss',
        'supportLoss',
        'investmentStress',
        'availableSavings',
        'investmentTimeHorizon',
        'percentageInvestment',
        'investmentDescription',
        'favoriteEvolution',
        'lifeInsuranceSouscription',
        'lifeInsuranceDown',
        'lifeInsuranceLosses',
        'lifeInsuranceCauses',
        'diferenceFondEurosAndUnity',
        'drivenManagement',
        'drivenFree',
        'discretionaryManagement',
        'investmentPeriod',
        'objectBeneficiaryClause',
        'clauseOrModels',
        'yesNoDurableInvestment',
        'investmentRatio',
        'advisorTakesEnviromentalCriteria',
        'choicesEnvironmentalCriteria',
        'advisorTakeSocialCriteria',
        'choicesSocialCriteria',
        'advisorTakeGovernanceCriteria',
        'choicesGovernanceCriteria',
        'lowerPerformanceForESGInvestments',
        'isPoliticallyExposedOptions',
    ].reduce((requiredValidators, field) => ({
        ...requiredValidators,
        [field]: requiredField,
    }), {})),
};

const validate = (values, { params: { target = 'client' }, getFieldName }) => {
    const applyValidators = (cible, errorsAcc) => Object.entries(validators).reduce((errors, [field, validator]) => {
        const name = getFieldName(cible, field);
        const value = get(name, values);
        const error = validator(value);

        return error ? set(name, error, errors) : errors;
    }, errorsAcc);

    if ('client&partner' === target) {
        // On valide pour les deux simultanément
        let errors = {};
        errors = applyValidators('client', errors);
        errors = applyValidators('partner', errors);

        return errors;
    }

    // Cas simple (client OU partner)
    return applyValidators(target, {});
};

const formProvider = flowRight([
    withRouter,
    // deny invalid targets
    branch(({ params: { target = 'client' } }) => !validTargets.includes(target), renderNothing),
    // get the investor profile
    connect(({ investorProfile }) => (({
        // provide the investorProfile in props
        investorProfile,
        // helper to get field names
        getFieldName: (cible, name) => `investorProfiles.${cible}.${name}`,
    }))),
    // set the data form (options can be provided)
    dataForm({
        autoSave: false,
        form: 'rip-investorProfile',
        id: ({ params: { id } }) => id,
        type: 'rip',
        // by default get back to the entry point on successful submits
        onSubmit: (result, dispatch, {
            getFieldName,
            navigate,
            investorProfile,
            params: { id, target = 'client' },
        }) => {
            const { investorProfiles } = result;
            // nettoyage des réponses à choix multiples si la réponse est non
            const cleanedResult = cleanInvestorProfileData(investorProfiles);
            // update the rip
            dispatch(saveDataInvestor('rip', id, {
                // update the rip with what we got
                investorProfiles: cleanedResult,
                // but removed signatures
                signature: null,
                partnerSignature: null,
                advisorSignature: null,
                // ** suppression des signatures manuelles ET électroniques lors d'une modification de l'AMF
                universignTransaction: null,
                universignTransactionState: null,
                clientAndPartnerSignatureDate: null,
                // and uncheck legal requirements
                clientCollectInformationsAgreement: false,
                clientCorrectInformationsAgreement: false,
                clientCorrectInformationsShoyo: false,
                partnerCollectInformationsAgreement: false,
                partnerCorrectInformationsAgreement: false,
                partnerCorrectInformationsShoyo: false,
                advisorAgreement: false,
            }));

            if ('client&partner' === target) {
                if (get(getFieldName('client', 'decline'), result) && get(getFieldName('partner', 'decline'), result)) {
                // return to origin
                    navigate(investorProfile.path);
                } else {
                    // jump to the next page
                    navigate(`/rip/${id}/investorProfile/${target}/result`);
                }
            } else if (get(getFieldName(target, 'decline'), result)) {
                // return to origin
                navigate(investorProfile.path);
            } else {
                // jump to the next page
                navigate(`/rip/${id}/investorProfile/${target}/result`);
            }
        },
        validate,
        // smooth scroll on errors
        onSubmitFail: scrollToFirstError({ containerId: 'investorProfileForm' }),
    }),
    // get the decline value
    formValues(({ getFieldName }) => ({
        decline: getFieldName('client', 'decline'),
        sufferTradingLosses: getFieldName('client', 'sufferTradingLosses'),
        lifeInsuranceLosses: getFieldName('client', 'lifeInsuranceLosses'),
        durableInvestment: getFieldName('client', 'yesNoDurableInvestment'),
        enviromentalCriteria: getFieldName('client', 'advisorTakesEnviromentalCriteria'),
        socialCriteria: getFieldName('client', 'advisorTakeSocialCriteria'),
        governanceCriteria: getFieldName('client', 'advisorTakeGovernanceCriteria'),
        declinePartner: getFieldName('partner', 'decline'),
        sufferTradingLossesPartner: getFieldName('partner', 'sufferTradingLosses'),
        lifeInsuranceLossesPartner: getFieldName('partner', 'lifeInsuranceLosses'),
        durableInvestmentPartner: getFieldName('partner', 'yesNoDurableInvestment'),
        enviromentalCriteriaPartner: getFieldName('partner', 'advisorTakesEnviromentalCriteria'),
        socialCriteriaPartner: getFieldName('partner', 'advisorTakeSocialCriteria'),
        governanceCriteriaPartner: getFieldName('partner', 'advisorTakeGovernanceCriteria'),
    })),
]);

export default formProvider;
